import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { SAVE_FILTERS_TEMPLATE } from '../../mutations';
import { DOCUMENT_TEMPLATE_FILTER_UPDATED } from '../../subscriptions';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import useGenericFilterData, { GenericFilterContext } from 'features/common/hooks/useGenericFilterData';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { Card } from '@totalsoft/rocket-ui';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import { useChangeTrackingLens, set, get } from '@totalsoft/change-tracking-react';
import FlexBox from 'components/common/FlexBox';

const actionsSlotId = 'document-template-filters-actions-slot';

const DocumentTemplateFiltersContainer = ({ condition, filters, documentTemplateId }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Side effects

  useEffect(() => {
    resetLens({
      withTemplate: false,
      templateId: null,
      template: {
        phrase: condition,
        conditions: filters.map(filter => ({
          id: filter.filterId,
          orderNo: filter.orderNo,
          paramId: filter.paramId,
          operatorId: filter.operatorId,
          value: filter.filterValue,
          valueId: filter.filterValueId,
          values: (filter.filterValues || []).map(value => ({
            id: value.valueId,
            detailId: value.filterId,
            value: value.filterValue,
            valueId: value.filterValueId
          }))
        }))
      }
    });
    setInitialData({
      withTemplate: false,
      templateId: null,
      template: {
        phrase: condition,
        conditions: filters.map(filter => ({
          id: filter.filterId,
          orderNo: filter.orderNo,
          paramId: filter.paramId,
          operatorId: filter.operatorId,
          value: filter.filterValue,
          valueId: filter.filterValueId,
          values: (filter.filterValues || []).map(value => ({
            id: value.valueId,
            detailId: value.filterId,
            value: value.filterValue,
            valueId: value.filterValueId
          }))
        }))
      }
    });
  }, [condition, filters, resetLens]);

  // #endregion

  // #region Queries

  const { parameters, operators, loadValues, loadPagedValues } = useGenericFilterData({
    context: GenericFilterContext.DocumentTemplate
  });

  // #endregion

  // #region Mutations

  const [saveDetails] = useMutation(SAVE_FILTERS_TEMPLATE, {
    onError: () => {
      (lens.disabled |> set)(false);
    }
  });

  // #endregion

  // #region Subscriptions

  useSubscription(DOCUMENT_TEMPLATE_FILTER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      client.cache.evict({ id: `DocumentTemplateItem:{"documentTemplateId": ${data.documentTemplateFilterUpdated.documentTemplateId}}` });
      addToast(t('GenericFilterTemplate.Saved'), 'success');
    }
  });

  // #endregion

  // #region Handlers

  const onHandleSave = data => {
    (lens.disabled |> set)(true);
    saveDetails({ variables: { input: { documentTemplateId, ...data.template } } });
  };

  // #endregion

  return (
    <Card
      icon={ViewQuiltIcon}
      iconColor='primary'
      title={t('EntityFilter.Filters')}
      actions={<FlexBox gap={0.5} alignItems='center' id={actionsSlotId}></FlexBox>}
    >
      <GenericFilterComposer
        lens={lens}
        dirtyInfo={dirtyInfo}
        resetLens={resetLens}
        parameters={parameters}
        operators={operators}
        onSave={onHandleSave}
        loadValues={loadValues}
        loadPagedValues={loadPagedValues}
        withTemplateGeneralData
        actionsSlotId={actionsSlotId}
      />
    </Card>
  );
};

DocumentTemplateFiltersContainer.propTypes = {
  condition: PropTypes.string.isRequired,
  filters: PropTypes.array.isRequired,
  documentTemplateId: PropTypes.number
};

export default DocumentTemplateFiltersContainer;
